import React, { createContext, useContext, useState, useCallback, useEffect, useRef, type ReactNode } from 'react';

/**
 * TemplateContext
 *
 * Architecture Decision 1 — template.types.ts is the canonical type definition.
 * This context re-exports TemplateCostCentre and ProjectTemplate directly from
 * template.types.ts so that all consumers reference the single source of truth.
 */

// Re-export canonical types so existing imports from '@/contexts/TemplateContext' keep working
export type { TemplateCostCentre, ProjectTemplate, TemplateCategory, TemplateResourceEntry } from '@/types/template.types';
import type { ProjectTemplate, TemplateCostCentre } from '@/types/template.types';
import {
  getDataMode,
  fetchProjectTemplates,
  createProjectTemplate,
  updateProjectTemplate as apiUpdateTemplate,
  deleteProjectTemplate as apiDeleteTemplate,
  incrementProjectTemplateUsage,
} from '@/lib/data-service';
import type { CreateTemplateData } from '@/types/template.types';

interface TemplateContextType {
  templates: ProjectTemplate[];
  addTemplate: (template: Omit<ProjectTemplate, 'id' | 'createdAt' | 'updatedAt'>) => void;
  updateTemplate: (id: string, updates: Partial<ProjectTemplate>) => void;
  deleteTemplate: (id: string) => void;
  toggleFavorite: (id: string) => void;
  getTemplate: (id: string) => ProjectTemplate | undefined;
}

const TemplateContext = createContext<TemplateContextType | undefined>(undefined);

// ── Seed Templates (Task 1.3) ──────────────────────────────────────────────────────────────────
// Updated with: templateName (renamed from name), category, defaultStatus, defaultDurationDays,
// objectives, keyDeliverables, usageCount, lastUsedAt.
// Removed: location, unit, tags.
// Enriched with realistic PSA cost centre codes per template type.
const initialMockTemplates: ProjectTemplate[] = [
  {
    id: '1',
    templateName: 'Software Development Project',
    templateDescription: 'Standard template for software development projects with phases and milestones',
    category: 'Infrastructure',
    description: 'A comprehensive software development project with agile methodology',
    priority: 'High',
    type: 'Fixed Price',
    defaultStatus: '1', // Planning
    defaultDurationDays: 90,
    currencyCode: 'USD',
    clients: '',
    costCentres: [
      { code: 'ENG-001', name: 'Engineering', type: 'Department', billable: true, status: 'Not Started', costsBudget: 0, revenueBudget: 0, hoursBudget: 0 },
      { code: 'ENG-002', name: 'QA & Testing', type: 'Department', billable: true, status: 'Not Started', costsBudget: 0, revenueBudget: 0, hoursBudget: 0 },
    ],
    resourceBudget: [
      { role: 'Project Manager' },
      { role: 'Technical Architect' },
      { role: 'Developer' },
      { role: 'QA Tester' },
    ],
    objectives:
      'Deliver a production-ready software solution that meets all agreed functional and non-functional requirements on time and within budget.',
    keyDeliverables:
      'Technical specification document\nSprint delivery reports\nDeployed application\nTest coverage report\nHandover documentation',
    isFavorite: true,
    usageCount: 14,
    lastUsedAt: new Date(Date.now() - 3 * 24 * 60 * 60 * 1000).toISOString(),
    createdAt: new Date('2024-01-01').toISOString(),
    updatedAt: new Date('2024-01-01').toISOString(),
  },
  {
    id: '2',
    templateName: 'Marketing Campaign',
    templateDescription: 'Template for marketing campaigns with budget tracking and deliverables',
    category: 'Marketing',
    description: 'Marketing campaign with social media and content strategy',
    priority: 'Medium',
    type: 'Time & Materials',
    defaultStatus: '1', // Planning
    defaultDurationDays: 45,
    currencyCode: 'USD',
    clients: '',
    costCentres: [
      { code: 'MKT-001', name: 'Marketing', type: 'Department', billable: true, status: 'Not Started', costsBudget: 0, revenueBudget: 0, hoursBudget: 0 },
      { code: 'MKT-002', name: 'Content & Creative', type: 'Team', billable: true, status: 'Not Started', costsBudget: 0, revenueBudget: 0, hoursBudget: 0 },
    ],
    resourceBudget: [
      { role: 'Project Manager' },
      { role: 'Consultant' },
    ],
    objectives:
      'Increase brand awareness and generate qualified leads through a targeted multi-channel campaign.',
    keyDeliverables:
      'Campaign brief\nContent calendar\nCreative assets\nCampaign performance report',
    isFavorite: true,
    usageCount: 8,
    lastUsedAt: new Date(Date.now() - 10 * 24 * 60 * 60 * 1000).toISOString(),
    createdAt: new Date('2024-01-02').toISOString(),
    updatedAt: new Date('2024-01-02').toISOString(),
  },
  {
    id: '3',
    templateName: 'Infrastructure Upgrade',
    templateDescription: 'IT infrastructure project template with resource allocation',
    category: 'Infrastructure',
    description: 'Infrastructure modernization and cloud migration project',
    priority: 'High',
    type: 'Fixed Price',
    defaultStatus: '1', // Planning
    defaultDurationDays: 60,
    currencyCode: 'USD',
    clients: '',
    costCentres: [
      { code: 'ITO-001', name: 'IT Operations', type: 'Department', billable: false, status: 'Not Started', costsBudget: 0, revenueBudget: 0, hoursBudget: 0 },
      { code: 'ITO-002', name: 'Cloud Services', type: 'Service', billable: false, status: 'Not Started', costsBudget: 0, revenueBudget: 0, hoursBudget: 0 },
      { code: 'ITO-003', name: 'Security', type: 'Service', billable: false, status: 'Not Started', costsBudget: 0, revenueBudget: 0, hoursBudget: 0 },
    ],
    resourceBudget: [
      { role: 'Project Manager' },
      { role: 'Technical Architect' },
      { role: 'Developer' },
    ],
    objectives:
      'Modernise the existing infrastructure to improve reliability, scalability, and security posture.',
    keyDeliverables:
      'Infrastructure assessment report\nMigration plan\nDeployed infrastructure\nSecurity review sign-off\nRunbook documentation',
    isFavorite: true,
    usageCount: 5,
    lastUsedAt: new Date(Date.now() - 20 * 24 * 60 * 60 * 1000).toISOString(),
    createdAt: new Date('2024-01-03').toISOString(),
    updatedAt: new Date('2024-01-03').toISOString(),
  },
  {
    id: '4',
    templateName: 'Product Launch',
    templateDescription: 'End-to-end product launch template with cross-functional tasks',
    category: 'Internal',
    description: 'Complete product launch with go-to-market strategy',
    priority: 'High',
    type: 'Fixed Price',
    defaultStatus: '1', // Planning
    defaultDurationDays: 30,
    currencyCode: 'USD',
    clients: '',
    costCentres: [
      { code: 'PRD-001', name: 'Product', type: 'Department', billable: false, status: 'Not Started', costsBudget: 0, revenueBudget: 0, hoursBudget: 0 },
      { code: 'PRD-002', name: 'Go-to-Market', type: 'Team', billable: true, status: 'Not Started', costsBudget: 0, revenueBudget: 0, hoursBudget: 0 },
    ],
    resourceBudget: [
      { role: 'Project Manager' },
      { role: 'Senior Consultant' },
      { role: 'Consultant' },
    ],
    objectives:
      'Successfully launch the product to market, achieving initial adoption targets and positive user feedback.',
    keyDeliverables:
      'Go-to-market strategy\nLaunch readiness checklist\nPress release\nPost-launch performance report',
    isFavorite: false,
    usageCount: 3,
    lastUsedAt: new Date(Date.now() - 45 * 24 * 60 * 60 * 1000).toISOString(),
    createdAt: new Date('2024-01-04').toISOString(),
    updatedAt: new Date('2024-01-04').toISOString(),
  },
  {
    id: '5',
    templateName: 'Consulting Engagement',
    templateDescription: 'Professional services template with time tracking and billing',
    category: 'Consulting',
    description: 'Consulting project with deliverables and milestones',
    priority: 'Medium',
    type: 'Time & Materials',
    defaultStatus: '1', // Planning
    defaultDurationDays: 20,
    currencyCode: 'USD',
    clients: '',
    costCentres: [
      { code: 'PS-001', name: 'Professional Services', type: 'Service', billable: true, status: 'Not Started', costsBudget: 0, revenueBudget: 0, hoursBudget: 0 },
      { code: 'PS-002', name: 'Expenses', type: 'Other', billable: true, status: 'Not Started', costsBudget: 0, revenueBudget: 0, hoursBudget: 0 },
    ],
    resourceBudget: [
      { role: 'Project Manager' },
      { role: 'Senior Consultant' },
      { role: 'Business Analyst' },
    ],
    objectives:
      'Provide expert guidance and actionable recommendations that deliver measurable value to the client.',
    keyDeliverables:
      'Discovery workshop outputs\nCurrent-state assessment\nRecommendations report\nImplementation roadmap',
    isFavorite: false,
    usageCount: 12,
    lastUsedAt: new Date(Date.now() - 7 * 24 * 60 * 60 * 1000).toISOString(),
    createdAt: new Date('2024-01-05').toISOString(),
    updatedAt: new Date('2024-01-05').toISOString(),
  },
];

const STORAGE_KEY = 'project-templates';

// ── localStorage hydration (Task 1.4) ─────────────────────────────────────────────────────────
// Merges stored user data with the latest seed defaults so new fields are always present.
// When a stored template matches a seed by ID, the seed provides default values for any
// missing fields (e.g. fields added in a later release), and stored values take precedence.
// New templates added to initialMockTemplates (new IDs) are appended automatically.
const loadTemplatesFromStorage = (): ProjectTemplate[] => {
  try {
    const stored = localStorage.getItem(STORAGE_KEY);
    if (stored) {
      const parsed: any[] = JSON.parse(stored);

      // Build a map of stored templates by ID
      const storedById = new Map<string, any>(parsed.map(t => [t.id, t]));

      // Build a map of seed templates by ID
      const seedById = new Map<string, ProjectTemplate>(
        initialMockTemplates.map(t => [t.id, t]),
      );

      // Hydrate stored templates — merge seed defaults + stored overrides
      const hydrated: ProjectTemplate[] = parsed.map((raw: any) => {
        const seed = seedById.get(raw.id);
        return {
          // Seed defaults first (so any new fields from the latest release are present)
          ...(seed ?? {}),
          // User's stored values override the seed (preserves edits, favorites, etc.)
          ...raw,
          // Normalise: if old data had `name` instead of `templateName`, back-fill it
          templateName: raw.templateName ?? raw.name ?? seed?.templateName ?? '',
          // Ensure usageCount and lastUsedAt are present (new fields)
          usageCount: raw.usageCount ?? seed?.usageCount ?? 0,
          lastUsedAt: raw.lastUsedAt ?? seed?.lastUsedAt ?? null,
          // Ensure category is present (new field)
          category: raw.category ?? seed?.category,
          // Ensure new content fields are present
          objectives: raw.objectives ?? seed?.objectives,
          keyDeliverables: raw.keyDeliverables ?? seed?.keyDeliverables,
          // 8.4 — Back-fill resourceBudget for templates stored before Phase 8
          resourceBudget: raw.resourceBudget ?? seed?.resourceBudget ?? [],
          defaultDurationDays:
            raw.defaultDurationDays !== undefined
              ? raw.defaultDurationDays
              : seed?.defaultDurationDays ?? null,
          defaultStatus: raw.defaultStatus ?? seed?.defaultStatus,
          // Remove legacy fields that no longer exist on the type
          // (they'll just be ignored by TypeScript, but strip them to keep storage clean)
        } as ProjectTemplate;
      });

      // Append any brand-new seed templates that weren't in storage yet
      for (const seed of initialMockTemplates) {
        if (!storedById.has(seed.id)) {
          hydrated.push(seed);
        }
      }

      return hydrated;
    }
  } catch (error) {
    console.error('Error loading templates from localStorage:', error);
  }
  // Nothing in storage — return the seed templates
  return initialMockTemplates;
};

const saveTemplatesToStorage = (templates: ProjectTemplate[]) => {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(templates));
  } catch (error) {
    console.error('Error saving templates to localStorage:', error);
  }
};

// ── Provider ───────────────────────────────────────────────────────────────────────────────────

export function TemplateProvider({ children }: { children: ReactNode }) {
  const mode = getDataMode();
  const isLive = mode === 'live';
  const [templates, setTemplates] = useState<ProjectTemplate[]>(() =>
    isLive ? [] : loadTemplatesFromStorage(),
  );
  const initialFetchDone = useRef(false);

  // Live mode: fetch templates from API on mount
  useEffect(() => {
    if (!isLive || initialFetchDone.current) return;
    initialFetchDone.current = true;
    fetchProjectTemplates()
      .then(setTemplates)
      .catch(err => console.error('Failed to fetch project templates:', err));
  }, [isLive]);

  // Demo/tour mode: persist to localStorage whenever templates change
  useEffect(() => {
    if (!isLive) {
      saveTemplatesToStorage(templates);
    }
  }, [templates, isLive]);

  const addTemplate = useCallback(
    (template: Omit<ProjectTemplate, 'id' | 'createdAt' | 'updatedAt'>) => {
      if (isLive) {
        const templateData = { ...template } as Record<string, unknown>;
        delete templateData.id;
        delete templateData.createdAt;
        delete templateData.updatedAt;
        createProjectTemplate(templateData as unknown as CreateTemplateData)
          .then(created => setTemplates(prev => [...prev, created]))
          .catch(err => console.error('Failed to create project template:', err));
      } else {
        const now = new Date().toISOString();
        const newTemplate: ProjectTemplate = {
          ...template,
          id: `template-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
          usageCount: template.usageCount ?? 0,
          lastUsedAt: template.lastUsedAt ?? undefined,
          createdAt: now,
          updatedAt: now,
        };
        setTemplates(prev => [...prev, newTemplate]);
      }
    },
    [isLive],
  );

  const updateTemplate = useCallback((id: string, updates: Partial<ProjectTemplate>) => {
    // Optimistic local update
    setTemplates(prev =>
      prev.map(template =>
        template.id === id
          ? { ...template, ...updates, updatedAt: new Date().toISOString() }
          : template,
      ),
    );

    if (isLive) {
      // In live mode, if only usageCount/lastUsedAt changed, use the dedicated endpoint
      const keys = Object.keys(updates);
      const isUsageOnly = keys.every(k => k === 'usageCount' || k === 'lastUsedAt');
      if (isUsageOnly && keys.includes('usageCount')) {
        incrementProjectTemplateUsage(id).catch(err =>
          console.error('Failed to increment template usage:', err),
        );
      } else {
        // Full update — merge current template with updates
        setTemplates(prev => {
          const current = prev.find(t => t.id === id);
          if (current) {
            const merged = { ...current, ...updates } as Record<string, unknown>;
            delete merged.createdAt;
            delete merged.updatedAt;
            delete merged.usageCount;
            delete merged.lastUsedAt;
            merged.id = id;
            apiUpdateTemplate(id, merged as Partial<CreateTemplateData> & { id: string }).catch(err =>
              console.error('Failed to update project template:', err),
            );
          }
          return prev;
        });
      }
    }
  }, [isLive]);

  const deleteTemplate = useCallback((id: string) => {
    setTemplates(prev => prev.filter(template => template.id !== id));
    if (isLive) {
      apiDeleteTemplate(id).catch(err =>
        console.error('Failed to delete project template:', err),
      );
    }
  }, [isLive]);

  const toggleFavorite = useCallback((id: string) => {
    setTemplates(prev => {
      const updated = prev.map(template =>
        template.id === id
          ? { ...template, isFavorite: !template.isFavorite, updatedAt: new Date().toISOString() }
          : template,
      );
      if (isLive) {
        const toggled = updated.find(t => t.id === id);
        if (toggled) {
          const payload = { ...toggled } as Record<string, unknown>;
          delete payload.createdAt;
          delete payload.updatedAt;
          delete payload.usageCount;
          delete payload.lastUsedAt;
          payload.id = id;
          apiUpdateTemplate(id, payload as Partial<CreateTemplateData> & { id: string }).catch(err =>
            console.error('Failed to toggle template favorite:', err),
          );
        }
      }
      return updated;
    });
  }, [isLive]);

  const getTemplate = useCallback(
    (id: string) => {
      return templates.find(template => template.id === id);
    },
    [templates],
  );

  return (
    <TemplateContext.Provider
      value={{
        templates,
        addTemplate,
        updateTemplate,
        deleteTemplate,
        toggleFavorite,
        getTemplate,
      }}
    >
      {children}
    </TemplateContext.Provider>
  );
}

export function useTemplates() {
  const context = useContext(TemplateContext);
  if (context === undefined) {
    throw new Error('useTemplates must be used within a TemplateProvider');
  }
  return context;
}
